import { BlockEditProps, registerBlockType } from "@wordpress/blocks";
import React from "react";
import config, { type BlockAttributes } from "./block.json";
import {
  BlockAlignmentToolbar,
  BlockControls,
  BlockVerticalAlignmentToolbar,
  InnerBlocks,
  useBlockProps,
} from "@wordpress/block-editor";
import { column } from "@wordpress/icons";
import useBlockEditorSelect from "../../../source/hooks/useBlockEditorSelect";

const breakpoints = ["xs", "sm", "md", "lg", "xl", "xxl", "xxxl"] as const;

// function rotateIcon(icon: JSX.Element): JSX.Element {
//   return React.cloneElement(icon, {
//     transform: 'rotate(90)'
//   })
// }
// export const verticalAlignTop = rotateIcon(justifyLeft)
// export const verticalAlignCenter = rotateIcon(justifyCenter)
// export const verticalAlignBottom = rotateIcon(justifyRight)

const alignmentClass: Record<
  Exclude<BlockAttributes["verticalAlign"], undefined>,
  string
> = {
  top: "justify-content-start",
  center: "justify-content-center",
  bottom: "justify-content-end",
};

const horizontalAlignmentClass: Record<
  Exclude<BlockAttributes["horizontalAlign"], undefined>,
  string
> = {
  none: "",
  left: "align-items-start",
  center: "align-items-center",
  right: "align-items-end",
};

function ColumnEditor(props: BlockEditProps<BlockAttributes>) {
  const classes = breakpoints
    .filter((x) => x in props.attributes)
    .map((x) => `col${x === "xs" ? "" : `-${x}`}-${props.attributes[x]}`);

  for (const bp of breakpoints) {
    const offsetName = `offset${bp[0].toUpperCase()}${bp.slice(1)}`;
    if (offsetName in props.attributes) {
      classes.push(
        `offset${bp === "xs" ? "" : `-${bp}`}-${props.attributes[offsetName]}`
      );
    }
  }

  classes.push(
    "d-flex",
    "flex-column",
    alignmentClass[props.attributes.verticalAlign ?? "top"],
    horizontalAlignmentClass[props.attributes.horizontalAlign ?? "none"]
  );

  const hasChildBlocks = useBlockEditorSelect(
    (select) => select.getBlockOrder(props.clientId).length > 0,
    [props.clientId]
  );

  const blockProps = useBlockProps({
    className: classes.filter((x) => !!x).join(" "),
  });

  return (
    <div {...blockProps}>
      <BlockControls controls>
        <BlockVerticalAlignmentToolbar
          onChange={(verticalAlign) => props.setAttributes({ verticalAlign })}
          value={props.attributes.verticalAlign}
        />
        <BlockAlignmentToolbar
          onChange={(alignmentValue) => {
            // If null or undefined is passed, set to "none"
            if (alignmentValue === null || alignmentValue === undefined) {
              props.setAttributes({ horizontalAlign: "none" });
              return;
            }

            // Only apply allowed values from our enum
            if (
              alignmentValue === "left" ||
              alignmentValue === "center" ||
              alignmentValue === "right"
            ) {
              props.setAttributes({
                horizontalAlign: alignmentValue,
              });
            }
          }}
          value={
            props.attributes.horizontalAlign === "none"
              ? undefined
              : props.attributes.horizontalAlign
          }
          controls={["left", "center", "right"]}
        />
      </BlockControls>
      <InnerBlocks
        templateLock={false}
        renderAppender={
          hasChildBlocks ? undefined : () => <InnerBlocks.ButtonBlockAppender />
        }
      />
    </div>
  );
}

registerBlockType<BlockAttributes>(config.name, {
  ...config,
  icon: column,

  edit: ColumnEditor,

  save() {
    return <InnerBlocks.Content />;
  },
});
